import { CropFree as CropFreeIcon } from "@mui/icons-material";
import {
  Autocomplete,
  CircularProgress,
  FilterOptionsState,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { ScannerPartSuggestDialog } from "components/barcodeScan/ScannerPartSuggestDialog";
import StyledTextField from "components/StyledTextField";
import { scanditLicense } from "env";
import { filterPartList, intlFormatPrice } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { useDebounce } from "hooks/useDebounce";
import { nanoid } from "nanoid";
import { PartType, Stock, StockStore } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { searchOrderStock } from "store/slices/stock.store";

export interface StockAutocompleteProps {
  isOrder?: boolean;
  selectedStock?: Stock;
  loading?: boolean;
  partList: PartType[];
  showQty: boolean;
  onChange: (selectedPart: PartType | null) => void;
  disableBarcodeScan?: boolean;
}

export const StockAutocomplete: FC<StockAutocompleteProps> = (props) => {
  const { isOrder, partList, selectedStock, loading, showQty, onChange, disableBarcodeScan } =
    props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);
  const [openScanner, setOpenScanner] = useState(false);
  const [openSuggested, setOpenSuggested] = useState(false);
  const [possibleMatches, setPossibleMatches] = useState<PartType[]>([]);
  const [scannedValue, setScannedValue] = useState("");
  const [partSearch, setPartSearch] = useState<string>("");
  const [partInputValue, setPartInputValue] = useState<string>("");
  const debouncedPartSearch = useDebounce<string>(partSearch, 500);
  const allowSalesPrice = technicianSettings.syncSalesPrice || technicianSettings.syncCostPrice;
  const hasBarcodeScanner = scanditLicense() && !disableBarcodeScan;

  const getPartLabel = useCallback(
    (part: PartType) => {
      let label = part.description;

      if (showQty && !part.isNonStock) {
        label += ` (${part.quantity})`;
      }
      return label ?? "";
    },
    [showQty],
  );

  useEffect(() => {
    if (selectedPart && getPartLabel(selectedPart) === debouncedPartSearch) return;
    if (selectedStock) {
      console.log(selectedPart, debouncedPartSearch);
      dispatch(
        searchOrderStock({
          locationId: selectedStock.id,
          searchString: debouncedPartSearch,
          isStockTracing: true,
        }),
      );
    }
  }, [debouncedPartSearch, dispatch, getPartLabel, selectedPart, selectedStock]);

  const findMatches = useCallback(
    (parts: PartType[], scannerValue: string) => {
      const testValue = scannerValue.toLowerCase();
      let scanMatches: PartType[] = [];
      let equalMatch: PartType | null = null;
      for (const part of parts) {
        const barcode = part.barcode?.toLowerCase() ?? "";
        const articleNo = part.articleNo?.toLowerCase() ?? "";

        if (barcode === testValue || articleNo === testValue) {
          equalMatch = part;
          break;
        } else if (barcode.includes(testValue) || articleNo.includes(testValue)) {
          scanMatches.push(part);
        }
      }

      if (equalMatch !== null) {
        setSelectedPart(equalMatch);
      } else {
        if (scanMatches.length === 0) {
          dispatch(addSnackbarMessage({ key: "No-barcode-match", argument: scannerValue }));
        } else {
          setPossibleMatches(scanMatches);
          setOpenSuggested(true);
        }
      }
    },
    [dispatch],
  );

  const onScan = useCallback(
    (scannerValue: string) => {
      setScannedValue(scannerValue);
      setOpenScanner(false);
      setPartInputValue(scannerValue);

      if (isOrder) {
        dispatch(
          searchOrderStock({
            locationId: selectedStock?.id,
            searchString: scannerValue,
            isStockTracing: true,
          }),
        )
          .then(unwrapResult)
          .then(({ stockEnquiry }) => {
            findMatches(stockEnquiry, scannerValue);
          });
      } else {
        findMatches(partList, scannerValue);
      }
    },
    [dispatch, findMatches, isOrder, partList, selectedStock?.id],
  );

  const handleCloseSuggest = useCallback(() => {
    setOpenSuggested(false);
    setScannedValue("");
  }, []);

  const onSelectPart = useCallback(
    (part: PartType | null) => {
      setSelectedPart(part);
      onChange(part);
    },
    [onChange],
  );

  return (
    <Grid item container alignItems="start" justifyContent="space-between" wrap="nowrap">
      <Autocomplete
        fullWidth
        data-testid="PartAutocomplete"
        options={partList}
        loading={loading}
        groupBy={(part) => {
          const stockStoreTranslatable =
            part.stockStore === StockStore.Engineer
              ? intl.formatMessage({ id: "general.engineer" })
              : part.stockStore === StockStore.Locational
                ? intl.formatMessage({ id: "part.locational" })
                : part.stockStore === StockStore.Nonstock
                  ? intl.formatMessage({ id: "part.nonStock" })
                  : part.stockStore === StockStore.Other
                    ? intl.formatMessage({ id: "part.other" })
                    : part.stockStore;

          return part.stockStore === StockStore.Locational
            ? (part.stockName?.toUpperCase() ?? "")
            : (stockStoreTranslatable?.toUpperCase() ?? "");
        }}
        getOptionLabel={getPartLabel}
        noOptionsText={intl.formatMessage({
          id: "general.noOptions",
        })}
        filterOptions={(options: PartType[], state: FilterOptionsState<PartType>) => {
          return filterPartList(options, state.inputValue);
        }}
        value={selectedPart}
        inputValue={partInputValue}
        onInputChange={(_, value) => {
          setPartSearch(value);
          setPartInputValue(value);
        }}
        onChange={(_, value) => onSelectPart(value)}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            label={intl.formatMessage({
              id: "part",
            })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, part: PartType) => (
          <li {...props} key={nanoid()} style={{ display: "block" }}>
            <Typography variant="body1">
              {part.description}
              {showQty && !part.isNonStock && ` (${part.quantity})`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {part.articleNo}
            </Typography>
            {allowSalesPrice && part.salesPrice && (
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="general.salesPrice" />:{" "}
                {intlFormatPrice(intl, part.salesPrice)}
              </Typography>
            )}
          </li>
        )}
      />
      {hasBarcodeScanner && (
        <>
          <Grid item>
            <IconButton
              data-testid="PartAutocomplete-BarcodeButton"
              onClick={() => setOpenScanner(true)}
              size="large"
              sx={{ pr: 0, ml: 1 }}
            >
              <CropFreeIcon fontSize="large" />
            </IconButton>
          </Grid>

          <BarcodeScannerDialog
            open={openScanner}
            setOpen={(open: boolean) => setOpenScanner(open)}
            submitValue={onScan}
          />

          <ScannerPartSuggestDialog
            open={openSuggested}
            scannerValue={scannedValue}
            parts={possibleMatches}
            selectPart={onSelectPart}
            onClose={handleCloseSuggest}
          />
        </>
      )}
    </Grid>
  );
};
